import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ConversationMessageToolConsentEnum,
  type ConversationMessageToolPart,
} from 'types/ai.types';

const useToolContent = (
  messagePart: ConversationMessageToolPart,
  isLastPart: boolean,
): string => {
  const { t, i18n } = useTranslation();

  const isAllowed =
    !messagePart.consent ||
    messagePart.consent === ConversationMessageToolConsentEnum.allowed;

  const suffix =
    !isLastPart && isAllowed ? 'done' : messagePart.consent || 'allowed';

  const toolArgs = useMemo(() => {
    try {
      return messagePart.args ? JSON.parse(messagePart.args) : {};
    } catch (e) {
      return {};
    }
  }, [messagePart.args]);

  const filtersPart = useMemo(
    () =>
      Object.keys(toolArgs)
        .filter((key) => {
          const isValidType =
            typeof toolArgs[key] === 'string' ||
            typeof toolArgs[key] === 'number';

          return isValidType && i18n.exists(`ai_bot.tool_args.${key}`);
        })
        .map((key) => t(`ai_bot.tool_args.${key}`, { value: toolArgs[key] }))
        .join(' '),
    [t, i18n, toolArgs],
  );

  if (!i18n.exists(`ai_bot.tool_content.${messagePart.name}.${suffix}`)) {
    return t(`ai_bot.tool_consent.fallback.${suffix}`);
  }

  // e.g., "Looking for expenses{{filters}}"
  return t(`ai_bot.tool_content.${messagePart.name}.${suffix}`, {
    filters: filtersPart ? ` ${filtersPart}` : '',
  });
};

export default useToolContent;
